import './SuggestionField.scss';
import { AutodocType } from '@sonar-software/sonar-core';
import classNames from 'classnames';
import { CloseCircleOutlined } from '@ant-design/icons';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { haltClickEventPropagation } from '@lib/events';
import { UseSuggestionEditorResult } from '@hooks';
import { Button, Skeleton } from 'antd';
import { Case, Default, Switch, When } from 'react-if';
import { OutsideClickWrapper, SuggestionButton } from '@ui/general';
import React, { PropsWithChildren, useCallback } from 'react';

export interface SuggestionEditorProps {
	autodocType: AutodocType;
	isSpreadsheet?: boolean;
	isReadOnly?: boolean;
	onAccept: () => void;
	onEdit: () => void;
	onBlur?: () => void;
	placeholder?: string;
	suggestion: UseSuggestionEditorResult;
}

export const SuggestionField: React.FC<PropsWithChildren<SuggestionEditorProps>> = ({
	autodocType,
	children,
	isSpreadsheet,
	onAccept,
	onEdit,
	onBlur,
	placeholder,
	suggestion
}) => {
	const handleClick = useCallback(() => {
		suggestion.request();
	}, [ suggestion ]);

	const handleSuggestionClick = useCallback(
		(e: React.MouseEvent | MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();
			onEdit();
		},
		[ onEdit ]
	);

	const handleAcceptClick = useCallback(
		(e: React.MouseEvent | MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();
			onAccept();
		},
		[ onAccept ]
	);

	return (
		<OutsideClickWrapper
			className='suggest-field'
			onClick={ suggestion.loading ? haltClickEventPropagation : handleSuggestionClick }
			onClickOutside={ onBlur }>
			<When condition={ !suggestion.isEditing }>
				<div
					className={ classNames('suggestion-overlay', {
						error: suggestion.hasError,
						spreadsheet: isSpreadsheet
					}) }>
					<Switch>
						<Case condition={ suggestion.loading }>
							<Skeleton.Input active />
						</Case>
						<Case condition={ suggestion.value }>
							<div className='suggestion-preview'>
								<div
									className='suggestion-text'
									onClick={ handleSuggestionClick }>
									{ suggestion.value?.split(' ').map((word, index) => {
										const key = `${word}${index}`;

										return <span key={ key }>{ word }</span>;
									}) }
								</div>
								<div className='accept-suggestion'>
									{ COPY_SPECIFIC.UNSAVED_SUGGESTION }
									<Button
										onClick={ handleAcceptClick }
										type='link'>
										{ COPY_SPECIFIC.ACCEPT }
									</Button>
								</div>
							</div>
						</Case>
						<Default>
							<When condition={ isSpreadsheet }>
								<SuggestionButton
									autodocType={ autodocType }
									onClick={ handleClick } />
							</When>
							<When condition={ placeholder }>
								<span className='placeholder'>{ placeholder }</span>
							</When>
						</Default>
					</Switch>
					<When condition={ isSpreadsheet }>
						<div className='error-icon'>
							<CloseCircleOutlined />
						</div>
					</When>
				</div>
			</When>
			<div className={ classNames('suggestion-editor', { hidden: !suggestion.isEditing }) }>{ children }</div>
		</OutsideClickWrapper>
	);
};
